import { useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Smartphone } from "lucide-react";
import { toast } from "sonner";

/**
 * Post-login modal that asks the user to add their phone for SMS login.
 *
 * Shows automatically after a successful password login when the user has no
 * phone on file. The user can:
 *  - Type their phone + Save → POSTs to /auth/me/phone → modal closes,
 *    AuthContext refreshes so the new phone is reflected immediately.
 *  - "Not now" → modal closes, we set a localStorage flag so it doesn't
 *    nag again for 7 days.
 *
 * Props:
 *   open / onOpenChange  — controlled state
 *   onSaved              — callback after a successful phone save
 */
export default function AddPhonePrompt({ open, onOpenChange, onSaved }) {
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const dismiss = () => {
    try {
      localStorage.setItem("ibx_phone_prompt_skipped_at", Date.now().toString());
    } catch {
      // localStorage may be unavailable (private mode); silently ignore.
    }
    onOpenChange(false);
  };

  const save = async () => {
    const v = phone.trim();
    if (v.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me/phone", { phone: v });
      toast.success("Phone saved — next time, sign in with a quick SMS code.");
      setPhone("");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save phone.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) dismiss(); }}>
      <DialogContent className="max-w-md" data-testid="add-phone-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            <Smartphone className="w-4 h-4 text-[#0033CC]" strokeWidth={2} />
            Sign in faster next time
          </DialogTitle>
          <DialogDescription className="text-sm">
            Add your phone number to sign in with a one-tap SMS code instead of typing your password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-[10px] font-semibold tracking-[0.05em] uppercase text-zinc-500">
            Mobile number
          </label>
          <Input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            className="h-11 text-base"
            data-testid="add-phone-input"
            autoFocus
          />
          <p className="text-[11px] text-zinc-500">
            We only use this for sign-in codes — never for marketing.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={dismiss}
            disabled={saving}
            data-testid="add-phone-skip"
          >
            Not now
          </Button>
          <Button
            onClick={save}
            disabled={saving || !phone.trim()}
            className="bg-[#0033CC] hover:bg-[#002299] text-white"
            data-testid="add-phone-save"
          >
            {saving ? "Saving…" : "Save phone"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
